// Upload template + parser for recording many months of the fixed-fee
// statement at once. One row per month; the five expense columns map to the
// statement's sections:
//   Salary -> Salaries, EOBI / Social Security / Monthly Expense -> Expenses,
//   TAX PRA -> Taxes paid.
import * as XLSX from "xlsx";
import { emptyStatement, normalizeStatement, statementTotals, validateStatement } from "./fixedFeeCalculator";

export const TEMPLATE_COLUMNS = [
  "Month", "Fixed Fee", "Salary", "EOBI", "Social Security", "Monthly Expense", "TAX PRA",
  "Service Charge", "Note", "Sales Tax Charged", "WHT Rate %", "WHT Withheld", "Sales Tax Withheld"
];

const MONTH_NAMES = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const norm = (value) => String(value ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "");
const text = (value) => String(value ?? "").trim();
const pad = (n) => String(n).padStart(2, "0");
const fullYear = (y) => (y < 100 ? 2000 + y : y);

export function parseMonth(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const shifted = new Date(value.getTime() + 12 * 3600 * 1000);
    return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}`;
  }
  const raw = text(value);
  let m = raw.match(/^(\d{4})[-/](\d{1,2})(?:[-/]\d{1,2})?$/);
  if (m && Number(m[2]) >= 1 && Number(m[2]) <= 12) return `${m[1]}-${pad(m[2])}`;
  m = raw.match(/^(\d{1,2})[-/](\d{4})$/);
  if (m && Number(m[1]) >= 1 && Number(m[1]) <= 12) return `${m[2]}-${pad(m[1])}`;
  m = raw.match(/^([A-Za-z]{3})[a-z]*[-/ ,.]*(\d{2,4})$/);
  if (m && MONTH_NAMES.includes(m[1].toLowerCase())) return `${fullYear(Number(m[2]))}-${pad(MONTH_NAMES.indexOf(m[1].toLowerCase()) + 1)}`;
  return "";
}

const num = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : NaN;
};

export function buildTemplateWorkbook() {
  const months = Array.from({ length: 12 }, (_, index) => `2026-${pad(index + 1)}`);
  const sheet = XLSX.utils.aoa_to_sheet([TEMPLATE_COLUMNS, ...months.map((month) => [month])]);
  sheet["!cols"] = TEMPLATE_COLUMNS.map((name) => ({ wch: Math.max(14, name.length + 2) }));
  const instructions = XLSX.utils.aoa_to_sheet([
    ["How to fill this sheet"],
    [""],
    ["One row per month. Fill the Month and Fixed Fee, then the expenses ALM paid that month. A row with no Fixed Fee and no amounts is ignored."],
    [""],
    ["Month", "Required. Write it as 2026-09 (year-month). Sep-2026 or 09/2026 also work."],
    ["Fixed Fee", "Required. The monthly fee BEFORE sales tax, e.g. 2997480."],
    ["Salary", "Salaries paid on actual working days (total for the month)."],
    ["EOBI", "EOBI paid for the month."],
    ["Social Security", "Social security (PESSI) paid for the month."],
    ["Monthly Expense", "Other monthly expenses (total)."],
    ["TAX PRA", "The PRA tax ALM ACTUALLY PAID for the month. If the client withheld part of the sales tax and deposited it themselves, that part is not yours to pay — enter only what ALM paid."],
    ["Service Charge", "Optional. Leave blank to use the fixed 80,000."],
    ["Note", "Optional note that the client will also see."],
    [""],
    ["Optional columns (leave blank to use the normal rule)"],
    ["Sales Tax Charged", "Blank = 16% of the fixed fee."],
    ["WHT Rate %", "Blank = 6% for months up to June 2026 and 7% from July 2026. Write 7 for 7%."],
    ["WHT Withheld", "Blank = WHT rate x total invoice. Type the exact figure if the client's payment differs."],
    ["Sales Tax Withheld", "Blank = 20% of the sales tax for months from September 2026, and 0 for earlier months. Type the exact figure if the client's payment differs."],
    [""],
    ["Importing a month that is already saved replaces it. Nothing is shown to the client until you publish."]
  ]);
  instructions["!cols"] = [{ wch: 22 }, { wch: 100 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Monthly Data");
  XLSX.utils.book_append_sheet(workbook, instructions, "Instructions");
  return workbook;
}

export function parseStatementSheet(buffer, clientCode = "CPBM") {
  const workbook = XLSX.read(buffer, { cellDates: true });
  const name = workbook.SheetNames.find((sheetName) => norm(sheetName) === "monthlydata") || workbook.SheetNames[0];
  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, defval: "", raw: true });
  const headerIndex = rows.findIndex((row) => row.map(norm).includes("month") && row.map(norm).includes("fixedfee"));
  if (headerIndex < 0) throw new Error("This does not look like the template — no Month and Fixed Fee header row was found. Download the template and use it.");
  const headers = rows[headerIndex].map(norm);
  const col = (...names) => headers.findIndex((header) => names.includes(header));
  const at = {
    month: col("month"), fee: col("fixedfee"), salary: col("salary"), eobi: col("eobi"), social: col("socialsecurity", "pessi"),
    monthly: col("monthlyexpense", "monthlyexpenses"), pra: col("taxpra", "pra"), service: col("servicecharge"), note: col("note", "notes"),
    tax: col("salestaxcharged"), whtRate: col("whtrate", "whtrate%"), whtAmount: col("whtwithheld"), taxWithheld: col("salestaxwithheld")
  };

  const statements = [];
  const errors = [];
  const seen = new Map();
  let ignored = 0;

  rows.slice(headerIndex + 1).forEach((row, offset) => {
    const excelRow = headerIndex + offset + 2;
    const get = (index) => (index >= 0 ? row[index] : "");
    const monthText = get(at.month);
    const values = {
      fee: num(get(at.fee)), salary: num(get(at.salary)), eobi: num(get(at.eobi)), social: num(get(at.social)),
      monthly: num(get(at.monthly)), pra: num(get(at.pra)), service: num(get(at.service)), tax: num(get(at.tax)),
      whtRate: num(get(at.whtRate)), whtAmount: num(get(at.whtAmount)), taxWithheld: num(get(at.taxWithheld))
    };
    const hasData = Object.values(values).some((value) => value !== null);
    if (!text(monthText) && !hasData) return;
    if (!hasData) { ignored += 1; return; }

    const period = parseMonth(monthText);
    if (!period) { errors.push(`Row ${excelRow}: the month "${text(monthText)}" could not be read — use 2026-09.`); return; }
    const bad = Object.entries(values).find(([, value]) => Number.isNaN(value));
    if (bad) { errors.push(`Row ${excelRow} (${period}): a value in the "${bad[0]}" column is not a number.`); return; }
    if (Object.entries(values).some(([key, value]) => value !== null && value < 0 && key !== "service")) { errors.push(`Row ${excelRow} (${period}): amounts cannot be negative.`); return; }
    if (!(values.fee > 0)) { errors.push(`Row ${excelRow} (${period}): the Fixed Fee is missing.`); return; }
    if (seen.has(period)) { errors.push(`Row ${excelRow}: ${period} appears twice (also on row ${seen.get(period)}).`); return; }
    seen.set(period, excelRow);

    const statement = emptyStatement(clientCode, period);
    statement.fee = values.fee;
    if (values.tax !== null) statement.taxCollected = values.tax;
    if (values.whtRate !== null) statement.whtRate = values.whtRate > 1 ? values.whtRate / 100 : values.whtRate;
    if (values.whtAmount !== null) statement.whtWithheld = values.whtAmount;
    if (values.taxWithheld !== null) statement.taxWithheld = values.taxWithheld;
    if (values.service !== null) statement.serviceCharge = values.service;
    statement.notes = text(get(at.note));
    const line = (section, description, amount) => { if (amount > 0) statement.lines.push({ section, description, amount, note: "" }); };
    line("SALARY", "Salary", values.salary);
    line("EXPENSE", "EOBI", values.eobi);
    line("EXPENSE", "Social Security", values.social);
    line("EXPENSE", "Monthly Expense", values.monthly);
    line("TAX", "TAX PRA", values.pra);

    const problem = validateStatement(statement);
    if (problem) { errors.push(`Row ${excelRow} (${period}): ${problem}`); return; }
    const clean = normalizeStatement(statement);
    statements.push({ ...clean, totals: statementTotals(clean), sheetRow: excelRow });
  });

  statements.sort((a, b) => a.period.localeCompare(b.period));
  return { statements, errors, ignored };
}
